import { Button, CircularProgress, Stack } from "@mui/material";
import { useUploadSecondDocumentMutation } from "Api/Mutations/Party/useUploadSecondDocumentMutation";
import {
  SECOND_DOCUMENT_TYPE_OPTIONS,
  SecondDocumentTypes,
  type SecondDocumentType,
} from "Components/Onboarding/shared/DocumentsUpload/types";
import { DragNDrop } from "Components/Shared/DragNDrop";
import { Dropdown } from "Components/Shared/Inputs/Dropdown";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Account.SecondDocumentUpload;
const FORM_RESOURCES = Resources.Onboarding.Steps.Documents.Forms;
const EXPIRATION_RESOURCES = Resources.Components.DocumentExpirationWarning;
const COMMON_RESOURCES = Resources.Common;

type Props = {
  partyPublicID: string;
  isExpired?: boolean;
};

export const SecondDocumentUpload = ({ partyPublicID, isExpired }: Props) => {
  const { t } = useResource();
  const [isFormVisible, setIsFormVisible] = useState(false);
  const [documentType, setDocumentType] = useState<SecondDocumentType | "">("");
  const [frontSide, setFrontSide] = useState<File | null>(null);
  const [backSide, setBackSide] = useState<File | null>(null);
  const { mutate: upload, isPending } = useUploadSecondDocumentMutation();

  const isDrivingLicense = documentType === SecondDocumentTypes.DrivingLicense;
  const isFormValid =
    !!documentType && !!frontSide && (!isDrivingLicense || !!backSide);

  const resetAndCloseForm = () => {
    setDocumentType("");
    setFrontSide(null);
    setBackSide(null);
    setIsFormVisible(false);
  };

  const handleUpload = () => {
    if (!frontSide || !documentType) {
      return;
    }

    upload(
      {
        frontSide,
        backSide: backSide ?? undefined,
        documentType,
        partyPublicID,
      },
      {
        onSuccess: resetAndCloseForm,
      },
    );
  };

  const handleDocumentTypeChange = (value: SecondDocumentType | "") => {
    setDocumentType(value);
    setFrontSide(null);
    setBackSide(null);
  };

  return (
    <Stack gap={2}>
      {!isFormVisible && (
        <Button
          variant="outlined"
          color={isExpired ? "error" : "primary"}
          onClick={() => {
            setIsFormVisible(true);
          }}
        >
          {t(EXPIRATION_RESOURCES.UploadNewDocument)}
        </Button>
      )}

      {isFormVisible && (
        <>
          <Dropdown
            name="secondDocumentType"
            label={t(FORM_RESOURCES.SecondDocumentTypeLabel)}
            value={documentType}
            options={SECOND_DOCUMENT_TYPE_OPTIONS.map((option) => ({
              Value: option.value,
              Name: t(option.labelKey),
            }))}
            onChange={(value) =>
              handleDocumentTypeChange(value as SecondDocumentType | "")
            }
          />

          {documentType && (
            <>
              <DragNDrop
                file={frontSide}
                onChange={setFrontSide}
                title={t(FORM_RESOURCES.SecondDocumentFrontSide)}
                description={t(RESOURCES.FileDescription)}
              />

              {isDrivingLicense && (
                <DragNDrop
                  file={backSide}
                  onChange={setBackSide}
                  title={t(FORM_RESOURCES.SecondDocumentBackSide)}
                  description={t(RESOURCES.FileDescription)}
                />
              )}
            </>
          )}

          <Button
            variant="contained"
            onClick={handleUpload}
            disabled={!isFormValid || isPending}
            startIcon={isPending ? <CircularProgress size={16} /> : undefined}
          >
            {t(RESOURCES.UploadButton)}
          </Button>
          <Button
            variant="text"
            onClick={resetAndCloseForm}
            disabled={isPending}
          >
            {t(COMMON_RESOURCES.Cancel)}
          </Button>
        </>
      )}
    </Stack>
  );
};
